import React, { PureComponent, ReactNode } from "react";
import styled from "styled-components";

import theme from "../../theme";
import Button from "../Button";
import Icon from "../Icon";
import Subtitle from "../Subtitle";
import ToggleSlide from "../ToggleSlide";

import { ErrorBlock } from "./components";

const BUTTON_OFFSET = 20;

interface IBlockWrapperProps {
  hasButton: boolean;
}

interface IBackground {
  noBackground: boolean;
}

const Wrapper = styled.div<IBackground & IBlockWrapperProps>`
  display: flex;
  flex-direction: column;
  margin-top: 0;
  background-color: ${(props: IBackground): string =>
    props.noBackground ? "transparent" : theme.colors.white};
  padding-top: 16px;
  padding-bottom: ${(props: IBlockWrapperProps): number =>
    props.hasButton ? 0 : 16}px;
  ${(props: IBlockWrapperProps): string =>
    props.hasButton ? `margin-bottom: ${BUTTON_OFFSET}px` : ""};
`;

type IFormBodyWrapperProps = Pick<IProps, "horizontalPadding">;

const BodyWrapper = styled.section`
  padding: ${(props: IFormBodyWrapperProps): string =>
    props.horizontalPadding ? `0 ${props.horizontalPadding}px` : "0 24px"};
`;

type ISubtitleWrapperProps = Pick<IProps, "toggleable" | "open"> & {
  hasTitle: boolean;
};

const SubtitleWrapper = styled.div`
  display: flex;
  position: relative;
  justify-content: center;
  padding: ${(props: ISubtitleWrapperProps): string =>
    props.toggleable ? "0 32px" : "0 16px"};

  margin-bottom: ${(props: ISubtitleWrapperProps): string =>
    !props.hasTitle || (props.hasTitle && props.toggleable && !props.open)
      ? "0px"
      : "16px"};
`;

const IconWrapper = styled.div`
  display: flex;
  position: absolute;
  align-self: center;
  right: 8px;
`;

const StyledButton = styled(Button)`
  flex-grow: 1;
  position: relative;
  margin: 0 ${theme.margins.marginBig};
  bottom: -${BUTTON_OFFSET}px;
`;

const StyledErrorBlock = styled(ErrorBlock)`
  margin-bottom: ${theme.margins.marginBase};
`;

export interface IProps {
  children: ReactNode[] | ReactNode;
  className?: string;
  errorMessage?: string;
  horizontalPadding?: number;
  immediate?: boolean;
  loading?: boolean;
  noBackground?: boolean;
  onClick?: () => void;
  onHeaderClick?: () => void;
  onRest?: () => void;
  open?: boolean;
  title?: string | ReactNode;
  toggleable?: boolean;
  validationText?: string;
}

export default class BlockWrapper extends PureComponent<IProps> {
  public static defaultProps: Partial<IProps> = {
    className: "",
    immediate: false,
    loading: false,
    noBackground: false,
    open: true,
    toggleable: false,
    validationText: "",
  };

  public render(): ReactNode {
    const {
      className,
      errorMessage,
      horizontalPadding,
      onClick,
      onHeaderClick,
      title,
      toggleable,
      validationText,
      open,
      noBackground,
      loading,
    } = this.props;

    return (
      <>
        {errorMessage && <StyledErrorBlock>{errorMessage}</StyledErrorBlock>}
        <Wrapper
          className={className}
          hasButton={!!onClick}
          noBackground={!!noBackground}
        >
          {title && (
            <SubtitleWrapper
              hasTitle={!!title}
              onClick={onHeaderClick}
              toggleable={toggleable}
              open={!!open}
            >
              <Subtitle as={"h2"}>{title}</Subtitle>
              <IconWrapper>{this.renderToggleIcon()}</IconWrapper>
            </SubtitleWrapper>
          )}
          <BodyWrapper
            className="bodywrapper"
            horizontalPadding={horizontalPadding}
          >
            {this.renderChildren()}
          </BodyWrapper>
          {onClick && (
            <StyledButton
              title={validationText!}
              onClick={onClick}
              loading={loading}
            />
          )}
        </Wrapper>
      </>
    );
  }

  public renderChildren(): ReactNode {
    const { children, immediate, onRest, open, toggleable } = this.props;

    if (toggleable) {
      return (
        <ToggleSlide
          duration={350}
          open={open!}
          immediate={immediate}
          onRest={onRest}
        >
          {children}
        </ToggleSlide>
      );
    }

    return children;
  }

  public renderToggleIcon(): ReactNode {
    const { open, toggleable } = this.props;

    if (!toggleable) {
      return null;
    }

    if (open) {
      return <Icon size={24} name="chevron-haut" />;
    }

    return <Icon size={24} name="chevron-bas" />;
  }
}
